import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Switch } from '../components/ui/switch';
import { useAuth } from '../context/AuthContext';
import { 
  Plus, 
  MoreHorizontal, 
  Pencil, 
  Trash2, 
  Key, 
  Users, 
  Shield,
  ShieldCheck,
  ShieldOff,
  Search,
  Loader2,
  UserPlus,
  Copy,
  Check,
  AlertCircle,
  Package,
  Eye,
  ChevronLeft,
  ChevronRight,
  KeyRound,
  MonitorX,
} from 'lucide-react';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const ROLE_OPTIONS = [
  { value: 'super_admin', label: 'Super Admin', description: 'Full system access' },
  { value: 'it_admin', label: 'IT Admin', description: 'IT related access' },
  { value: 'hr_admin', label: 'HR Admin', description: 'HR related access' },
  { value: 'viewer', label: 'Viewer', description: 'View only access' },
];

const ROLE_COLORS = {
  super_admin: 'bg-purple-100 text-purple-700 hover:bg-purple-100',
  it_admin: 'bg-blue-100 text-blue-700 hover:bg-blue-100',
  hr_admin: 'bg-green-100 text-green-700 hover:bg-green-100',
  viewer: 'bg-slate-100 text-slate-700 hover:bg-slate-100',
};

const AdminUsers = () => {
  const { user: currentUser, startImpersonation } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [subApps, setSubApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [pageSize, setPageSize] = useState(100);
  const [pagination, setPagination] = useState({ page: 1, page_size: 100, total: 0, total_pages: 0 });
  const [roleCounts, setRoleCounts] = useState({ total: 0, super_admin: 0, it_admin: 0, hr_admin: 0, viewer: 0 });
  
  // Dialog states
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [accessDialogOpen, setAccessDialogOpen] = useState(false);
  const [resetPasswordDialogOpen, setResetPasswordDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [tempPassword, setTempPassword] = useState('');
  const [copied, setCopied] = useState(false);
  
  // Form states
  const [formData, setFormData] = useState({
    username: '',
    password: '',
    full_name: '',
    email: '',
    role: 'viewer',
    is_temporary_password: false
  });
  const [formLoading, setFormLoading] = useState(false);
  const [formError, setFormError] = useState('');

  // Debounce search input (resets to page 1)
  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchQuery.trim());
      setPagination(prev => ({ ...prev, page: 1 }));
    }, 350);
    return () => clearTimeout(t);
  }, [searchQuery]);

  // Fetch sub-apps once
  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/subapps/all`);
        setSubApps(res.data);
      } catch (err) {
        // silent
      }
    })();
  }, []);

  // Fetch users whenever page, page size, or search changes
  useEffect(() => {
    fetchUsers(pagination.page, pageSize, debouncedSearch);
  }, [pagination.page, pageSize, debouncedSearch]);

  // Fetch role counts (independent of pagination) so stat cards stay accurate
  useEffect(() => {
    fetchRoleCounts();
  }, []);

  const fetchUsers = async (page = 1, size = pageSize, search = '') => {
    try {
      setLoading(true);
      const params = new URLSearchParams({ page: String(page), page_size: String(size) });
      if (search) params.set('search', search);
      const res = await axios.get(`${API}/users?${params.toString()}`);
      if (res.data?.data && res.data?.pagination) {
        setUsers(res.data.data);
        setPagination(res.data.pagination);
      } else {
        setUsers(res.data || []);
      }
    } catch (err) {
      toast.error('Failed to load users');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoleCounts = async () => {
    try {
      // Fetch all users once just for accurate role-card stats (max page_size 200)
      // We loop pages if total > 200
      let allUsers = [];
      let page = 1;
      while (true) {
        const res = await axios.get(`${API}/users?page=${page}&page_size=200`);
        const batch = res.data?.data || [];
        allUsers = allUsers.concat(batch);
        const totalPages = res.data?.pagination?.total_pages || 1;
        if (page >= totalPages) break;
        page += 1;
      }
      setRoleCounts({
        total: allUsers.length,
        super_admin: allUsers.filter(u => u.role === 'super_admin').length,
        it_admin: allUsers.filter(u => u.role === 'it_admin').length,
        hr_admin: allUsers.filter(u => u.role === 'hr_admin').length,
        viewer: allUsers.filter(u => u.role === 'viewer').length,
      });
    } catch (err) {
      // silent
    }
  };

  const refreshAll = () => {
    fetchUsers(pagination.page, pageSize, debouncedSearch);
    fetchRoleCounts();
  };

  const handleCreateUser = async () => {
    if (!formData.username || !formData.password) {
      setFormError('Username and password are required');
      return;
    }
    
    setFormLoading(true);
    setFormError('');
    
    try {
      await axios.post(`${API}/users`, formData);
      toast.success('User created successfully');
      setCreateDialogOpen(false);
      resetForm();
      refreshAll();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to create user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;
    
    setFormLoading(true);
    setFormError('');
    
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, {
        full_name: formData.full_name,
        email: formData.email,
        role: formData.role,
        is_active: formData.is_active
      });
      toast.success('User updated successfully');
      setEditDialogOpen(false);
      resetForm();
      refreshAll();
    } catch (err) {
      setFormError(err.response?.data?.detail || 'Failed to update user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleDeleteUser = async () => {
    if (!selectedUser) return;
    
    setFormLoading(true);
    
    try {
      await axios.delete(`${API}/users/${selectedUser.id}`);
      toast.success('User deleted successfully');
      setDeleteDialogOpen(false);
      setSelectedUser(null);
      refreshAll();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete user');
    } finally {
      setFormLoading(false);
    }
  };

  const handleResetPassword = async () => {
    if (!selectedUser) return;
    
    setFormLoading(true);
    
    try {
      const response = await axios.post(`${API}/users/${selectedUser.id}/reset-password`);
      setTempPassword(response.data.temporary_password);
      toast.success('Password reset successfully');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
      setResetPasswordDialogOpen(false);
    } finally {
      setFormLoading(false);
    }
  };

  const handleUpdateAccess = async (appId, hasAccess) => {
    if (!selectedUser) return;
    
    const currentAccess = selectedUser.app_access || [];
    let newAccess;
    
    if (hasAccess) {
      newAccess = [...currentAccess, appId];
    } else {
      newAccess = currentAccess.filter(id => id !== appId);
    }
    
    try {
      await axios.put(`${API}/users/${selectedUser.id}`, {
        app_access: newAccess
      });
      
      // Update local state
      setSelectedUser(prev => ({ ...prev, app_access: newAccess }));
      setUsers(prev => prev.map(u => 
        u.id === selectedUser.id ? { ...u, app_access: newAccess } : u
      ));
      
      toast.success('Access updated');
    } catch (err) {
      toast.error('Failed to update access');
    }
  };

  const handleReset2FA = async (userId, username) => {
    try {
      await axios.post(`${API}/auth/2fa/reset/${userId}`);
      setUsers(prev => prev.map(u =>
        u.id === userId ? { ...u, two_factor_enabled: false } : u
      ));
      toast.success(`2FA reset for ${username}. They will need to set up 2FA again on next login.`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset 2FA');
    }
  };

  const handleRevokeAllPasskeys = async (userId, username) => {
    // Compromise-recovery admin action — nukes every passkey the user
    // has registered. Confirm before running because it's an explicit
    // "we suspect this account is compromised" gesture, not a routine
    // cleanup. Frontend confirm; backend enforces super_admin.
    if (!window.confirm(
      `Revoke every passkey for ${username}?\n\n`
      + `They'll be forced back to password + 2FA on their next login `
      + `and can re-enrol passkeys from Profile once they're back in. `
      + `Use this if their passkey device (laptop / phone / security key) `
      + `is lost or stolen.`
    )) return;
    try {
      const r = await axios.post(`${API}/auth/passkeys/${userId}/revoke-all`);
      const n = r.data?.revoked_count ?? 0;
      toast.success(`Revoked ${n} passkey${n === 1 ? '' : 's'} for ${username}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke passkeys');
    }
  };

  const handleRevokeAllSessions = async (userId, username) => {
    // Admin session-nuke — kills every auth_sessions row for the user.
    // Their next protected API call from any device returns 401 and
    // the frontend bounces to /login. Pair with a password reset for a
    // full "kick this account off the platform right now" workflow.
    if (!window.confirm(
      `Sign ${username} out of every device?\n\n`
      + `Every browser they're currently signed in on will be kicked to `
      + `the login page on the next request. Their password and 2FA are `
      + `NOT changed by this action.`
    )) return;
    try {
      const r = await axios.post(`${API}/auth/sessions/${userId}/revoke-all`);
      const n = r.data?.revoked_count ?? 0;
      toast.success(`Signed out ${n} session${n === 1 ? '' : 's'} for ${username}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to revoke sessions');
    }
  };

  const handleToggle2FAExempt = async (userId, username, currentExempt) => {
    try {
      const res = await axios.post(`${API}/auth/2fa/exempt/${userId}`);
      setUsers(prev => prev.map(u => 
        u.id === userId ? { ...u, two_factor_exempt: !currentExempt } : u
      ));
      toast.success(res.data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update 2FA setting');
    }
  };

  const handleImpersonate = async (user) => {
    const result = await startImpersonation(user.id);
    if (result.success) {
      toast.success(`Now viewing as ${user.full_name || user.username}`);
      navigate('/dashboard');
    } else {
      toast.error(result.error || 'Failed to impersonate user');
    }
  };

  const resetForm = () => {
    setFormData({
      username: '',
      password: '',
      full_name: '',
      email: '',
      role: 'viewer',
      is_temporary_password: false
    });
    setFormError('');
    setSelectedUser(null);
    setTempPassword('');
    setCopied(false);
  };

  const openEditDialog = (user) => {
    setSelectedUser(user);
    setFormData({
      username: user.username,
      password: '',
      full_name: user.full_name || '',
      email: user.email || '',
      role: user.role,
      is_active: user.is_active
    });
    setEditDialogOpen(true);
  };

  const openAccessDialog = (user) => {
    setSelectedUser(user);
    setAccessDialogOpen(true);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-users-page">
        {/* Admin Sub-Nav */}
        <div className="flex gap-2 border-b">
          <Button variant="ghost" size="sm" className="rounded-none border-b-2 border-primary text-primary">
            <Users className="w-4 h-4 mr-2" /> Users
          </Button>
          <Button variant="ghost" size="sm" onClick={() => navigate('/admin/settings')} className="rounded-none border-b-2 border-transparent hover:border-muted">
            <Shield className="w-4 h-4 mr-2" /> System Settings
          </Button>
        </div>

        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-2">
              <Shield className="w-8 h-8 text-primary" />
              User Management
            </h1>
            <p className="text-muted-foreground mt-1">
              Manage users and their access to applications
            </p>
          </div>
          <Button 
            onClick={() => setCreateDialogOpen(true)}
            className="gap-2"
            data-testid="create-user-btn"
          >
            <UserPlus className="w-4 h-4" />
            Create User
          </Button>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardContent className="pt-6">
              <div className="flex items-center gap-4">
                <div className="p-3 rounded-lg bg-primary/10">
                  <Users className="w-6 h-6 text-primary" />
                </div>
                <div>
                  <p className="text-2xl font-bold" data-testid="stat-total-users">{roleCounts.total}</p>
                  <p className="text-sm text-muted-foreground">Total Users</p>
                </div>
              </div>
            </CardContent>
          </Card>
          
          {ROLE_OPTIONS.slice(0, 3).map(role => (
            <Card key={role.value}>
              <CardContent className="pt-6">
                <div className="flex items-center gap-4">
                  <div className={`p-3 rounded-lg ${ROLE_COLORS[role.value].replace('text-', 'bg-').replace('-700', '-500/20')}`}>
                    <Shield className="w-6 h-6" style={{ color: ROLE_COLORS[role.value].includes('purple') ? '#7c3aed' : ROLE_COLORS[role.value].includes('blue') ? '#2563eb' : '#16a34a' }} />
                  </div>
                  <div>
                    <p className="text-2xl font-bold">
                      {roleCounts[role.value] || 0}
                    </p>
                    <p className="text-sm text-muted-foreground">{role.label}s</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>

        {/* Search and Table */}
        <Card>
          <CardHeader className="pb-4">
            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
              <CardTitle>All Users</CardTitle>
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search users..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="search-users"
                />
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="w-8 h-8 animate-spin text-primary" />
              </div>
            ) : (
              <div className="rounded-md border overflow-hidden">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>User</TableHead>
                      <TableHead>Role</TableHead>
                      <TableHead className="hidden md:table-cell">App Access</TableHead>
                      <TableHead className="hidden sm:table-cell">Status</TableHead>
                      <TableHead className="hidden md:table-cell">2FA</TableHead>
                      <TableHead className="w-[50px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id} className="table-row-hover" data-testid={`user-row-${user.id}`}>
                        <TableCell>
                          <div>
                            <p className="font-medium">{user.full_name || user.username}</p>
                            <p className="text-sm text-muted-foreground">{user.email || user.username}</p>
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge className={ROLE_COLORS[user.role]}>
                            {ROLE_OPTIONS.find(r => r.value === user.role)?.label || user.role}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          <div className="flex items-center gap-1">
                            <Package className="w-4 h-4 text-muted-foreground" />
                            <span className="text-sm">
                              {user.role === 'super_admin' 
                                ? 'All Apps' 
                                : `${user.app_access?.length || 0} apps`}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="hidden sm:table-cell">
                          {user.is_active ? (
                            <Badge variant="outline" className="text-green-600 border-green-200">
                              Active
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-red-600 border-red-200">
                              Inactive
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="hidden md:table-cell">
                          {user.two_factor_exempt ? (
                            <Badge variant="outline" className="text-slate-500 border-slate-200 gap-1">
                              <ShieldOff className="w-3 h-3" />
                              Exempt
                            </Badge>
                          ) : user.two_factor_enabled ? (
                            <Badge variant="outline" className="text-green-600 border-green-200 gap-1">
                              <ShieldCheck className="w-3 h-3" />
                              Enabled
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="text-amber-600 border-amber-200 gap-1">
                              <ShieldOff className="w-3 h-3" />
                              Pending Setup
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon" data-testid={`user-actions-${user.id}`}>
                                <MoreHorizontal className="w-4 h-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuLabel>Actions</DropdownMenuLabel>
                              <DropdownMenuSeparator />
                              <DropdownMenuItem onClick={() => openEditDialog(user)}>
                                <Pencil className="mr-2 w-4 h-4" />
                                Edit User
                              </DropdownMenuItem>
                              {user.role !== 'super_admin' && (
                                <DropdownMenuItem 
                                  onClick={() => handleImpersonate(user)}
                                  data-testid={`impersonate-user-${user.id}`}
                                >
                                  <Eye className="mr-2 w-4 h-4" />
                                  Impersonate
                                </DropdownMenuItem>
                              )}
                              {user.role !== 'super_admin' && (
                                <DropdownMenuItem onClick={() => openAccessDialog(user)}>
                                  <Package className="mr-2 w-4 h-4" />
                                  Manage Access
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem 
                                onClick={() => {
                                  setSelectedUser(user);
                                  setResetPasswordDialogOpen(true);
                                }}
                              >
                                <Key className="mr-2 w-4 h-4" />
                                Reset Password
                              </DropdownMenuItem>
                              {user.two_factor_enabled && (
                                <DropdownMenuItem 
                                  onClick={() => handleReset2FA(user.id, user.username)}
                                >
                                  <ShieldOff className="mr-2 w-4 h-4" />
                                  Reset 2FA Device
                                </DropdownMenuItem>
                              )}
                              <DropdownMenuItem
                                onClick={() => handleToggle2FAExempt(user.id, user.username, user.two_factor_exempt)}
                              >
                                {user.two_factor_exempt ? (
                                  <><ShieldCheck className="mr-2 w-4 h-4" />Enforce 2FA</>
                                ) : (
                                  <><ShieldOff className="mr-2 w-4 h-4" />Disable 2FA</>
                                )}
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleRevokeAllPasskeys(user.id, user.username)}
                                data-testid={`revoke-passkeys-${user.id}`}
                              >
                                <KeyRound className="mr-2 w-4 h-4" />
                                Revoke All Passkeys
                              </DropdownMenuItem>
                              <DropdownMenuItem
                                onClick={() => handleRevokeAllSessions(user.id, user.username)}
                                data-testid={`revoke-sessions-${user.id}`}
                              >
                                <MonitorX className="mr-2 w-4 h-4" />
                                Sign Out All Devices
                              </DropdownMenuItem>
                              {user.role !== 'super_admin' && (
                                <>
                                  <DropdownMenuSeparator />
                                  <DropdownMenuItem 
                                    className="text-destructive focus:text-destructive"
                                    onClick={() => {
                                      setSelectedUser(user);
                                      setDeleteDialogOpen(true);
                                    }}
                                  >
                                    <Trash2 className="mr-2 w-4 h-4" />
                                    Delete User
                                  </DropdownMenuItem>
                                </>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                    {users.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center py-8 text-muted-foreground" data-testid="no-users-row">
                          {debouncedSearch ? `No users match "${debouncedSearch}"` : 'No users found'}
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            )}

            {/* Pagination Footer */}
            {!loading && pagination.total > 0 && (
              <div className="flex flex-col sm:flex-row items-center justify-between gap-3 mt-4 px-2" data-testid="users-pagination">
                <div className="text-sm text-muted-foreground">
                  Showing{' '}
                  <span className="font-medium text-foreground" data-testid="pagination-range-start">
                    {(pagination.page - 1) * pagination.page_size + 1}
                  </span>
                  {'–'}
                  <span className="font-medium text-foreground" data-testid="pagination-range-end">
                    {Math.min(pagination.page * pagination.page_size, pagination.total)}
                  </span>{' '}
                  of{' '}
                  <span className="font-medium text-foreground" data-testid="pagination-total">
                    {pagination.total}
                  </span>{' '}
                  users
                </div>
                <div className="flex items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Label htmlFor="page-size-select" className="text-sm text-muted-foreground whitespace-nowrap">
                      Rows per page
                    </Label>
                    <Select
                      value={String(pageSize)}
                      onValueChange={(val) => {
                        setPageSize(Number(val));
                        setPagination(prev => ({ ...prev, page: 1 }));
                      }}
                    >
                      <SelectTrigger id="page-size-select" className="w-[90px] h-9" data-testid="page-size-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="50">50</SelectItem>
                        <SelectItem value="100">100</SelectItem>
                        <SelectItem value="200">200</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.max(1, prev.page - 1) }))}
                      disabled={pagination.page <= 1}
                      data-testid="pagination-prev-btn"
                    >
                      <ChevronLeft className="w-4 h-4" />
                      Previous
                    </Button>
                    <span className="text-sm text-muted-foreground px-2" data-testid="pagination-page-indicator">
                      Page {pagination.page} of {pagination.total_pages || 1}
                    </span>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPagination(prev => ({ ...prev, page: Math.min(prev.total_pages || 1, prev.page + 1) }))}
                      disabled={pagination.page >= (pagination.total_pages || 1)}
                      data-testid="pagination-next-btn"
                    >
                      Next
                      <ChevronRight className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Create User Dialog */}
        <Dialog open={createDialogOpen} onOpenChange={(open) => { setCreateDialogOpen(open); if (!open) resetForm(); }}>
          {/* Same cap as Manage App Access. This form does not grow with data,
              but it is the TALLEST dialog on the page: ~674px, and ~718px once
              the validation banner below renders. A maximised browser on a
              1366x768 laptop has ~660px, so today the error a user needs to
              read and the Create User button they need to press are both
              partly off-screen — precisely when something has gone wrong. */}
          <DialogContent className="sm:max-w-md flex flex-col max-h-[85vh]">
            <DialogHeader className="shrink-0">
              <DialogTitle>Create New User</DialogTitle>
              <DialogDescription>
                Add a new user to the system
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
              {formError && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {formError}
                </div>
              )}
              
              <div className="space-y-2">
                <Label htmlFor="create-username">Username *</Label>
                <Input
                  id="create-username"
                  value={formData.username}
                  onChange={(e) => setFormData({ ...formData, username: e.target.value })}
                  placeholder="Enter username"
                  data-testid="create-username-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-password">Password *</Label>
                <Input
                  id="create-password"
                  type="password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  placeholder="Enter password"
                  data-testid="create-password-input"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-fullname">Full Name</Label>
                <Input
                  id="create-fullname"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-email">Email</Label>
                <Input
                  id="create-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="create-role">Role *</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                >
                  <SelectTrigger data-testid="create-role-select">
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div>
                          <p>{role.label}</p>
                          <p className="text-xs text-muted-foreground">{role.description}</p>
                        </div>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center space-x-2">
                <Checkbox
                  id="temp-password"
                  checked={formData.is_temporary_password}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_temporary_password: checked })}
                />
                <Label htmlFor="temp-password" className="text-sm font-normal">
                  Require password change on first login
                </Label>
              </div>
            </div>
            
            <DialogFooter className="shrink-0">
              <Button variant="outline" onClick={() => setCreateDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleCreateUser} disabled={formLoading} data-testid="create-user-submit">
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Create User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Edit User Dialog */}
        <Dialog open={editDialogOpen} onOpenChange={(open) => { setEditDialogOpen(open); if (!open) resetForm(); }}>
          {/* ~610-650px with the error banner shown: fits a normal laptop,
              not a short window. Same treatment so Save Changes is never the
              part that falls off. */}
          <DialogContent className="sm:max-w-md flex flex-col max-h-[85vh]">
            <DialogHeader className="shrink-0">
              <DialogTitle>Edit User</DialogTitle>
              <DialogDescription>
                Update user information
              </DialogDescription>
            </DialogHeader>
            
            <div className="space-y-4 py-4 flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
              {formError && (
                <div className="flex items-center gap-2 p-3 rounded-md bg-destructive/10 text-destructive text-sm">
                  <AlertCircle className="w-4 h-4" />
                  {formError}
                </div>
              )}
              
              <div className="space-y-2">
                <Label>Username</Label>
                <Input value={selectedUser?.username || ''} disabled className="bg-muted" />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-fullname">Full Name</Label>
                <Input
                  id="edit-fullname"
                  value={formData.full_name}
                  onChange={(e) => setFormData({ ...formData, full_name: e.target.value })}
                  placeholder="Enter full name"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-email">Email</Label>
                <Input
                  id="edit-email"
                  type="email"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="Enter email"
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="edit-role">Role</Label>
                <Select
                  value={formData.role}
                  onValueChange={(value) => setFormData({ ...formData, role: value })}
                  disabled={selectedUser?.role === 'super_admin'}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select role" />
                  </SelectTrigger>
                  <SelectContent>
                    {ROLE_OPTIONS.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        {role.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              <div className="flex items-center justify-between">
                <Label htmlFor="edit-active">Account Active</Label>
                <Switch
                  id="edit-active"
                  checked={formData.is_active}
                  onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  disabled={selectedUser?.role === 'super_admin'}
                />
              </div>
            </div>
            
            <DialogFooter className="shrink-0">
              <Button variant="outline" onClick={() => setEditDialogOpen(false)}>
                Cancel
              </Button>
              <Button onClick={handleUpdateUser} disabled={formLoading}>
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete User Dialog */}
        <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Delete User</DialogTitle>
              <DialogDescription>
                Are you sure you want to delete <strong>{selectedUser?.full_name || selectedUser?.username}</strong>? 
                This action cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button 
                variant="destructive" 
                onClick={handleDeleteUser} 
                disabled={formLoading}
                data-testid="confirm-delete-user"
              >
                {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                Delete User
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Manage Access Dialog */}
        <Dialog open={accessDialogOpen} onOpenChange={(open) => { setAccessDialogOpen(open); if (!open) setSelectedUser(null); }}>
          {/* Capped to the viewport with the LIST as the scrolling region, so
              the title, the close X and the Done button all stay put.
              Uncapped, this dialog grew with the number of sub-apps and —
              being centred with translate-y-[-50%] — spilled off BOTH ends:
              header above the fold, footer below it, with the last few apps
              unreachable. (Esc and overlay-click still closed it; it was the
              content that became unreachable, not the dialog.)

              Scrolling the body rather than DialogContent is deliberate — the
              primitive renders its close button `absolute`, so it stays
              pinned to the capped box while the middle moves.

              cn() is twMerge-backed, so `flex` here correctly overrides the
              primitive's `grid`. */}
          <DialogContent className="sm:max-w-md flex flex-col max-h-[85vh]">
            <DialogHeader className="shrink-0">
              <DialogTitle>Manage App Access</DialogTitle>
              <DialogDescription>
                Configure which applications <strong>{selectedUser?.full_name || selectedUser?.username}</strong> can access
              </DialogDescription>
            </DialogHeader>

            {/* min-h-0 is belt-and-braces: overflow-y-auto already sets the
                automatic minimum size to 0. -mx-1 px-1 keeps each row's left
                edge flush with the header once the scrollbar appears. */}
            <div className="space-y-4 py-4 flex-1 min-h-0 overflow-y-auto -mx-1 px-1">
              {subApps.length === 0 ? (
                <p className="text-center text-muted-foreground py-4">
                  No applications available
                </p>
              ) : (
                subApps.map(app => (
                  <div 
                    key={app.id} 
                    className="flex items-center justify-between p-3 rounded-lg border"
                  >
                    <div className="flex items-center gap-3">
                      <Package className="w-5 h-5 text-primary" />
                      <div>
                        <p className="font-medium text-sm">{app.name}</p>
                        <p className="text-xs text-muted-foreground line-clamp-1">
                          {app.description}
                        </p>
                      </div>
                    </div>
                    <Switch
                      checked={selectedUser?.app_access?.includes(app.id) || false}
                      onCheckedChange={(checked) => handleUpdateAccess(app.id, checked)}
                      data-testid={`access-toggle-${app.id}`}
                    />
                  </div>
                ))
              )}
            </div>
            
            <DialogFooter className="shrink-0">
              <Button onClick={() => setAccessDialogOpen(false)}>
                Done
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reset Password Dialog */}
        <Dialog open={resetPasswordDialogOpen} onOpenChange={(open) => { setResetPasswordDialogOpen(open); if (!open) resetForm(); }}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle>Reset Password</DialogTitle>
              <DialogDescription>
                {tempPassword 
                  ? 'Password has been reset. Share this temporary password with the user.'
                  : `Reset password for ${selectedUser?.full_name || selectedUser?.username}?`
                }
              </DialogDescription>
            </DialogHeader>
            
            {tempPassword ? (
              <div className="py-4">
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg">
                  <code className="flex-1 font-mono text-sm">{tempPassword}</code>
                  <Button 
                    variant="ghost" 
                    size="icon"
                    onClick={() => copyToClipboard(tempPassword)}
                  >
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground mt-2">
                  The user will be prompted to change this password on next login.
                </p>
              </div>
            ) : null}
            
            <DialogFooter>
              {tempPassword ? (
                <Button onClick={() => setResetPasswordDialogOpen(false)}>
                  Done
                </Button>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setResetPasswordDialogOpen(false)}>
                    Cancel
                  </Button>
                  <Button onClick={handleResetPassword} disabled={formLoading}>
                    {formLoading ? <Loader2 className="w-4 h-4 animate-spin mr-2" /> : null}
                    Reset Password
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </Layout>
  );
};

export default AdminUsers;
